import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import Meta from '../ass_components/Meta';
import BreadCrumb from '../ass_components/BreadCrumb';
import { translate } from '../i18n';
import { requestPasswordSetupApi, setPasswordApi } from '../servics/authApi';
import { loginSuccess } from '../store/reducers/authSlice';
import { passwordViolation } from '../utils/passwordPolicy';

/** Pays des bases ASSIGANTO (en-tête X-Tenant-Country). */
const COUNTRIES = ['TG', 'BJ', 'CI', 'SN', 'CM', 'GH', 'NG', 'ML', 'BF', 'NE'];

/**
 * /set-password?token=…&tenant=…&country=… — lien reçu par email à la création du compte (jamais de
 * mot de passe par email) ou redemandé. Le jeton est retiré de la barre d'adresse dès l'ouverture ;
 * lien expiré ou déjà utilisé : un nouveau lien peut être demandé.
 */
const SetPassword = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const language = useSelector((state) => state.lang?.lang) || 'FR';
    const selectedCountry = useSelector((state) => state.country?.country?.value);
    const text = translate('password_change', language) || translate('password_change', 'FR');

    // Lus une fois : le jeton ne reste ni dans l'URL ni dans l'historique.
    const [link] = useState(() => ({
        token: params.get('token') || '',
        tenant: params.get('tenant') || 'non_company',
        country: COUNTRIES.includes((params.get('country') || '').toUpperCase()) ? params.get('country').toUpperCase() : null,
    }));
    useEffect(() => {
        if (params.has('token')) window.history.replaceState(window.history.state, '', window.location.pathname);
    }, [params]);

    const [country, setCountry] = useState(link.country || (COUNTRIES.includes(selectedCountry) ? selectedCountry : 'TG'));
    const [values, setValues] = useState({ next: '', confirm: '' });
    const [errors, setErrors] = useState({});
    const [failure, setFailure] = useState(null);
    const [expired, setExpired] = useState(!link.token);
    const [pending, setPending] = useState(false);

    const set = (field) => (event) => setValues({ ...values, [field]: event.target.value });

    const onSubmit = async (event) => {
        event.preventDefault();
        const found = {};
        const violation = passwordViolation(values.next, null);
        if (violation) found.next = violation;
        if (values.confirm !== values.next) found.confirm = 'MISMATCH';
        setErrors(found);
        setFailure(null);
        if (Object.keys(found).length) return;

        setPending(true);
        try {
            const { data } = await setPasswordApi(link.token, values.next, link.tenant, country);
            const claims = jwtDecode(data.token);
            const userData = { ...claims, token: data.token };
            localStorage.setItem('token', data.token);
            localStorage.setItem('user', JSON.stringify(userData));
            if (claims.country_code) localStorage.setItem('country_code', claims.country_code);
            if (claims.tenant_code) localStorage.setItem('tenantCode', claims.tenant_code);
            dispatch(loginSuccess(userData));
            navigate('/account', { replace: true });
        } catch (error) {
            const code = error?.response?.data?.error;
            if (code === 'INVALID_OR_EXPIRED_LINK') setExpired(true);
            else if (typeof code === 'string' && code.startsWith('PASSWORD_')) setErrors({ next: code });
            else setFailure(text.failure);
        } finally {
            setPending(false);
        }
    };

    const field = (name, label) => (
        <div className="password-change-page__field">
            <label htmlFor={`setpwd-${name}`}>{label}</label>
            <input id={`setpwd-${name}`} type="password" className="form-control" autoComplete="new-password"
                   value={values[name]} onChange={set(name)} aria-invalid={Boolean(errors[name])} />
            {errors[name] && <span className="password-change-page__error" role="alert">{text.errors[errors[name]]}</span>}
        </div>
    );

    return (
        <>
            <Meta title={text.set_title} />
            <BreadCrumb title={text.set_title} />
            <div className="assiganto-home-wrapper-2 py-5">
                <div className="container-xxl">
                    <div className="auth-card password-change-page">
                        <h3 className="text-center mb-3">{text.set_title}</h3>
                        <p className="text-center">{text.set_subtitle}</p>
                        {expired ? (
                            <NewLinkRequest text={text} tenant={link.tenant} country={country} />
                        ) : (
                            <form className="d-flex flex-column gap-15" onSubmit={onSubmit} noValidate aria-busy={pending}>
                                {failure && <p className="password-change-page__error" role="alert">{failure}</p>}
                                {!link.country && (
                                    <div className="password-change-page__field">
                                        <label htmlFor="setpwd-country">{text.country}</label>
                                        <select id="setpwd-country" className="form-control" value={country} onChange={(e) => setCountry(e.target.value)}>
                                            {COUNTRIES.map((code) => <option key={code} value={code}>{code}</option>)}
                                        </select>
                                    </div>
                                )}
                                {field('next', text.new)}
                                <small className="password-change-page__hint">{text.rules}</small>
                                {field('confirm', text.confirm)}
                                <div className="d-flex justify-content-center">
                                    <button type="submit" className="button" disabled={pending}>{pending ? text.saving : text.set_save}</button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

/** Lien expiré, déjà utilisé ou incomplet : réponse identique que le compte existe ou non. */
const NewLinkRequest = ({ text, tenant, country }) => {
    const [email, setEmail] = useState('');
    const [state, setState] = useState('idle');

    const onSubmit = async (event) => {
        event.preventDefault();
        if (!/^\S+@\S+\.\S+$/.test(email.trim())) return;
        setState('pending');
        try {
            await requestPasswordSetupApi(email, tenant, country);
            setState('sent');
        } catch {
            setState('error');
        }
    };

    if (state === 'sent') return <p className="password-change-page__success" role="status">{text.request_sent}</p>;
    return (
        <form className="d-flex flex-column gap-15" onSubmit={onSubmit} noValidate aria-busy={state === 'pending'}>
            <p className="password-change-alert password-change-alert--inline" role="alert">{text.expired}</p>
            <div className="password-change-page__field">
                <label htmlFor="setpwd-email">{text.email}</label>
                <input id="setpwd-email" type="email" className="form-control" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            {state === 'error' && <p className="password-change-page__error" role="alert">{text.failure}</p>}
            <div className="d-flex justify-content-center">
                <button type="submit" className="button" disabled={state === 'pending'}>{text.request_action}</button>
            </div>
            <Link className="text-center" to="/login">{text.login}</Link>
        </form>
    );
};

export default SetPassword;
